/**
 * Editor de texto rico com formatação ABNT.
 *
 * Escrito do zero sobre `contenteditable`, sem bibliotecas. As ações de
 * ênfase usam `document.execCommand` (ainda o caminho mais compatível para
 * edição em contenteditable) e as ações de bloco são aplicadas diretamente
 * nos elementos de parágrafo, garantindo saída previsível e ABNT-conforme.
 */

import { h, clear } from './dom.js';
import { printScript } from './print.js';

export interface RichTextEditorOptions {
  value?: string;
  placeholder?: string;
  minHeight?: number;
  readOnly?: boolean;
  /** Rótulo usado no cabeçalho da exportação em PDF. */
  documentTitle?: string;
  documentSubtitle?: string;
  onChange?: (html: string) => void;
}

export interface RichTextEditorHandle {
  element: HTMLElement;
  surface: HTMLElement;
  getHtml(): string;
  getText(): string;
  setHtml(html: string): void;
  focus(): void;
}

const BLOCK_SELECTOR = 'p,div,h1,h2,h3,h4,h5,h6,blockquote,li';

/** Presets normativos (NBR 14724 / 6024 / 10520). */
const ABNT = {
  fontBody: "'Times New Roman', 'Liberation Serif', Times, serif",
  body: {
    fontFamily: "'Times New Roman', 'Liberation Serif', Times, serif",
    fontSize: '12pt',
    lineHeight: '1.5',
    textAlign: 'justify',
    textIndent: '1.25cm',
    margin: '0 0 0.4em',
  } as Partial<CSSStyleDeclaration>,
  heading: {
    fontFamily: "'Times New Roman', 'Liberation Serif', Times, serif",
    fontSize: '12pt',
    fontWeight: '700',
    lineHeight: '1.5',
    textAlign: 'left',
    textIndent: '0',
    margin: '1.2em 0 0.6em',
  } as Partial<CSSStyleDeclaration>,
  citation: {
    fontFamily: "'Times New Roman', 'Liberation Serif', Times, serif",
    fontSize: '10pt',
    fontWeight: '400',
    lineHeight: '1',
    textAlign: 'justify',
    textIndent: '0',
    marginLeft: '4cm',
    marginRight: '0',
    marginBottom: '0.8em',
  } as Partial<CSSStyleDeclaration>,
};

const PARAGRAPH_STYLES: Array<{ value: string; label: string }> = [
  { value: 'p', label: 'Corpo do texto' },
  { value: 'h1', label: 'Título primário' },
  { value: 'h2', label: 'Título secundário' },
  { value: 'h3', label: 'Título terciário' },
  { value: 'blockquote', label: 'Citação longa (ABNT)' },
];

const FONT_FAMILIES: Array<{ value: string; label: string }> = [
  { value: ABNT.fontBody, label: 'Times New Roman' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: "'Calibri', sans-serif", label: 'Calibri' },
  { value: "'Georgia', serif", label: 'Georgia' },
];

const FONT_SIZES = ['10pt', '11pt', '12pt', '14pt', '16pt', '18pt'];

const LINE_SPACINGS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Simples (1,0)' },
  { value: '1.15', label: '1,15' },
  { value: '1.5', label: '1,5 (ABNT)' },
  { value: '2', label: 'Duplo (2,0)' },
];

export function richTextEditor(options: RichTextEditorOptions = {}): RichTextEditorHandle {
  let savedRange: Range | null = null;

  /* ---------- Superfície editável -------------------------------------- */

  const surface = h('div', {
    class: 'rte__surface abnt-doc',
    contenteditable: options.readOnly ? 'false' : 'true',
    spellcheck: 'true',
    dataset: { placeholder: options.placeholder ?? 'Escreva o roteiro…' },
    style: { minHeight: `${options.minHeight ?? 320}px` },
    attrs: { role: 'textbox', 'aria-multiline': 'true' },
  }) as HTMLElement;

  if (options.value) surface.innerHTML = options.value;

  const counter = h('span', { class: 'rte__counter', text: '0 palavras' });

  const updateCounter = (): void => {
    const text = getText();
    const words = text.split(/\s+/).filter(Boolean).length;
    const chars = text.replace(/\s/g, '').length;
    counter.textContent = `${words} palavra${words === 1 ? '' : 's'} · ${chars} caractere${chars === 1 ? '' : 's'}`;
  };

  const emitChange = (): void => {
    updateCounter();
    options.onChange?.(surface.innerHTML);
  };

  surface.addEventListener('input', emitChange);
  surface.addEventListener('keyup', saveSelection);
  surface.addEventListener('mouseup', saveSelection);
  surface.addEventListener('blur', saveSelection);
  surface.addEventListener('paste', (event: ClipboardEvent) => {
    // Cola apenas texto, evitando trazer estilos externos para o documento.
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text) return;
    const html = text
      .split(/\n{2,}/)
      .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
      .join('');
    document.execCommand('insertHTML', false, html);
    saveSelection();
    emitChange();
  });

  /* ---------- Seleção --------------------------------------------------- */

  function saveSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!surface.contains(range.commonAncestorContainer)) return;
    savedRange = range.cloneRange();
  }

  function restoreSelection(): void {
    surface.focus();
    if (!savedRange) return;
    const selection = window.getSelection();
    if (!selection) return;
    try {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    } catch {
      /* a seleção pode ter sido invalidada por uma edição anterior */
    }
  }

  function exec(command: string, value?: string): void {
    restoreSelection();
    try {
      document.execCommand(command, false, value);
    } catch {
      /* comando indisponível: ignora silenciosamente */
    }
    saveSelection();
  }

  /** Blocos (parágrafos, títulos, itens) que a seleção atual atravessa. */
  function selectedBlocks(): HTMLElement[] {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return [];
    const range = selection.getRangeAt(0);

    const candidates = Array.from(surface.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
    const hit = candidates.filter((element) => range.intersectsNode(element));

    if (hit.length === 0) {
      let node: Node | null = range.startContainer;
      while (node && node !== surface) {
        if (node instanceof HTMLElement && node.matches(BLOCK_SELECTOR)) return [node];
        node = node.parentNode;
      }
      return [];
    }

    // Mantém apenas os blocos mais internos (evita aplicar em <ul> e <li>).
    return hit.filter(
      (element) => !hit.some((other) => other !== element && element.contains(other)),
    );
  }

  function applyToBlocks(style: Partial<CSSStyleDeclaration>): void {
    restoreSelection();
    const blocks = selectedBlocks();
    for (const block of blocks) Object.assign(block.style, style);
    saveSelection();
    emitChange();
  }

  /* ---------- Ações ABNT ------------------------------------------------ */

  const applyAbntBody = (): void => {
    restoreSelection();
    exec('formatBlock', '<p>');
    applyToBlocks(ABNT.body);
  };

  const applyAbntHeading = (): void => {
    applyToBlocks(ABNT.heading);
  };

  const applyAbntCitation = (): void => {
    restoreSelection();
    exec('formatBlock', '<blockquote>');
    applyToBlocks(ABNT.citation);
  };

  const setParagraphStyle = (tag: string): void => {
    restoreSelection();
    exec('formatBlock', `<${tag}>`);
    saveSelection();

    if (tag === 'blockquote') applyAbntCitation();
    else if (tag === 'p') applyAbntBody();
    else applyAbntHeading();
  };

  const clearFormatting = (): void => {
    restoreSelection();
    try {
      document.execCommand('removeFormat', false);
    } catch {
      /* ignora */
    }
    saveSelection();
    applyToBlocks({
      fontFamily: ABNT.fontBody,
      fontWeight: '400',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'justify',
      textIndent: '1.25cm',
      lineHeight: '1.5',
      marginLeft: '0',
      fontSize: '12pt',
    });
  };

  /* ---------- Barra de ferramentas ------------------------------------- */

  function tool(
    label: string,
    title: string,
    onClick: () => void,
    extraClass = '',
  ): HTMLButtonElement {
    const element = h('button', {
      class: `rte__btn ${extraClass}`.trim(),
      text: label,
      attrs: { type: 'button', title },
    }) as HTMLButtonElement;

    // Impede que o clique no botão remova a seleção do texto.
    element.addEventListener('mousedown', (event) => event.preventDefault());
    element.addEventListener('click', onClick);
    return element;
  }

  function toolSelect(
    choices: Array<{ value: string; label: string }>,
    title: string,
    onChange: (value: string) => void,
    initial?: string,
  ): HTMLSelectElement {
    const element = h('select', {
      class: 'rte__select',
      attrs: { title },
    }) as HTMLSelectElement;

    for (const choice of choices) {
      element.append(h('option', { value: choice.value, text: choice.label }));
    }
    if (initial) element.value = initial;

    element.addEventListener('mousedown', saveSelection);
    element.addEventListener('change', () => {
      restoreSelection();
      onChange(element.value);
    });

    return element;
  }

  const paragraphSelect = toolSelect(
    PARAGRAPH_STYLES,
    'Estilo do parágrafo',
    (value) => setParagraphStyle(value),
  );

  const fontSelect = toolSelect(
    FONT_FAMILIES,
    'Fonte',
    (value) => applyToBlocks({ fontFamily: value }),
    ABNT.fontBody,
  );

  const sizeSelect = toolSelect(
    FONT_SIZES.map((size) => ({ value: size, label: size.replace('pt', '') })),
    'Tamanho da fonte',
    (value) => applyToBlocks({ fontSize: value }),
    '12pt',
  );

  const spacingSelect = toolSelect(
    LINE_SPACINGS,
    'Entrelinha',
    (value) => applyToBlocks({ lineHeight: value }),
    '1.5',
  );

  const toolbar = h(
    'div',
    { class: 'rte__toolbar' },
    h(
      'div',
      { class: 'rte__group' },
      paragraphSelect,
      fontSelect,
      sizeSelect,
      spacingSelect,
    ),
    h(
      'div',
      { class: 'rte__group' },
      tool('B', 'Negrito (Ctrl+B)', () => exec('bold'), 'rte__btn--bold'),
      tool('I', 'Itálico (Ctrl+I)', () => exec('italic'), 'rte__btn--italic'),
      tool('U', 'Sublinhado (Ctrl+U)', () => exec('underline'), 'rte__btn--underline'),
      tool('S', 'Tachado', () => exec('strikeThrough')),
      tool('x²', 'Sobrescrito', () => exec('superscript')),
      tool('x₂', 'Subscrito', () => exec('subscript')),
    ),
    h(
      'div',
      { class: 'rte__group' },
      tool('⯇', 'Alinhar à esquerda', () => applyToBlocks({ textAlign: 'left' })),
      tool('≡', 'Centralizar', () => applyToBlocks({ textAlign: 'center' })),
      tool('⯈', 'Alinhar à direita', () => applyToBlocks({ textAlign: 'right' })),
      tool('☰', 'Justificar (ABNT)', () => applyToBlocks({ textAlign: 'justify' })),
    ),
    h(
      'div',
      { class: 'rte__group' },
      tool('•', 'Lista com marcadores', () => exec('insertUnorderedList')),
      tool('1.', 'Lista numerada', () => exec('insertOrderedList')),
      tool('⇥', 'Aumentar recuo', () => exec('indent')),
      tool('⇤', 'Diminuir recuo', () => exec('outdent')),
    ),
    h(
      'div',
      { class: 'rte__group rte__group--abnt' },
      tool('Corpo ABNT', 'Aplica fonte 12 pt, entrelinha 1,5, justificado e recuo de 1,25 cm', applyAbntBody, 'rte__btn--wide'),
      tool('Título ABNT', 'Aplica formatação de título (12 pt, negrito, alinhado à esquerda)', applyAbntHeading, 'rte__btn--wide'),
      tool('Citação longa', 'Citação com mais de 3 linhas: 10 pt, entrelinha simples, recuo de 4 cm', applyAbntCitation, 'rte__btn--wide'),
    ),
    h(
      'div',
      { class: 'rte__group' },
      tool('Desfazer', 'Desfazer (Ctrl+Z)', () => exec('undo')),
      tool('Refazer', 'Refazer (Ctrl+Shift+Z)', () => exec('redo')),
      tool('Limpar', 'Remover formatação', clearFormatting),
      tool('Exportar PDF', 'Gerar PDF nas margens e tipografia ABNT', () =>
        printScript(
          options.documentTitle ?? 'Roteiro',
          options.documentSubtitle ?? '',
          surface.innerHTML,
        ),
      ),
    ),
  );

  /* ---------- Montagem -------------------------------------------------- */

  const wrapper = h(
    'div',
    { class: 'rte' },
    options.readOnly ? null : toolbar,
    h('div', { class: 'rte__canvas' }, surface),
    h(
      'div',
      { class: 'rte__footer' },
      h('span', {
        class: 'rte__hint',
        text: 'ABNT: A4 · margens 3 cm/2 cm · fonte 12 pt · entrelinha 1,5 · recuo 1,25 cm',
      }),
      counter,
    ),
  );

  if (options.readOnly) wrapper.classList.add('rte--readonly');

  updateCounter();

  function getText(): string {
    return (surface.innerText || surface.textContent || '').replace(/\u00a0/g, ' ').trim();
  }

  return {
    element: wrapper,
    surface,
    getHtml: () => surface.innerHTML,
    getText,
    setHtml: (html: string) => {
      surface.innerHTML = html;
      emitChange();
    },
    focus: () => {
      surface.focus();
    },
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Renderiza HTML já sanitizado (roteiro no púlpito, pré-visualizações). */
export function renderScriptHtml(container: HTMLElement, html: string | null, fallback = ''): void {
  clear(container);
  if (html && html.trim()) {
    container.innerHTML = html;
    return;
  }
  container.textContent = fallback;
}
